import { Input } from "@/components/ui/input";
import FdeSelect from "@/frontend/features/Fde/FdeSelect";
import type { SourceSchema } from "@/frontend/features/Fde/data/types";

interface Props {
  schema: SourceSchema;
  value: unknown;
  path?: string[];
  required?: string[];
  configuredFields?: string[];
  configuredSecretPlaceholder?: string;
  onChange: (path: string[], value: unknown) => void;
}

function labelFor(key: string, schema: SourceSchema) {
  return (
    schema.title ||
    key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter: string) => letter.toUpperCase())
  );
}

function secretField(path: string[], schema: SourceSchema) {
  return (
    schema.airbyte_secret ||
    schema.airbyteSecret ||
    /password|secret|token|api.?key/i.test(path.join("_"))
  );
}

export function SourceSchemaFields({
  schema,
  value,
  path = [],
  required = [],
  configuredFields = [],
  configuredSecretPlaceholder,
  onChange,
}: Props) {
  const properties = schema.properties || {};

  return (
    <div className="space-y-4">
      {Object.entries(properties).map(([key, field]) => {
        const fieldPath = [...path, key];
        const record = (
          value && typeof value === "object" ? value : {}
        ) as Record<string, unknown>;
        const current = record[key] ?? field.default ?? "";
        const label = labelFor(key, field);
        const isRequired = required.includes(key);

        if (field.properties) {
          return (
            <fieldset key={key} className="rounded-lg border border-border p-4">
              <legend className="px-1 text-sm font-medium text-foreground">
                {label}
              </legend>
              <SourceSchemaFields
                schema={field}
                value={current}
                path={fieldPath}
                required={field.required}
                configuredFields={configuredFields}
                configuredSecretPlaceholder={configuredSecretPlaceholder}
                onChange={onChange}
              />
            </fieldset>
          );
        }

        if (field.type === "boolean") {
          return (
            <label
              key={key}
              className="flex items-start gap-3 rounded-md border border-border p-3 text-sm"
            >
              <input
                type="checkbox"
                checked={Boolean(current)}
                onChange={(event) => onChange(fieldPath, event.target.checked)}
                className="mt-0.5 size-4"
              />
              <span>
                <span className="font-medium text-foreground">{label}</span>
                {field.description && (
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {field.description}
                  </span>
                )}
              </span>
            </label>
          );
        }

        return (
          <div key={key} className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">
              {label}
              {isRequired && <span className="text-destructive"> *</span>}
            </span>
            {field.enum ? (
              <FdeSelect ariaLabel={label} value={String(current)} onValueChange={(next) => onChange(fieldPath, field.type === "integer" && next !== "" ? Number(next) : next)} placeholder="—" required={isRequired} options={[{ value: "", label: "—", disabled: isRequired }, ...field.enum.map((option, index) => ({ value: String(option), label: field.enumNames?.[index] || String(option) }))]} />
            ) : (
              <Input
                aria-label={label}
                type={
                  secretField(fieldPath, field)
                    ? "password"
                    : field.type === "number" || field.type === "integer"
                      ? "number"
                      : "text"
                }
                value={
                  Array.isArray(current) ? current.join(", ") : String(current)
                }
                required={isRequired}
                placeholder={secretField(fieldPath, field) && configuredFields.includes(fieldPath.join(".")) ? configuredSecretPlaceholder : undefined}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (field.type === "array")
                    onChange(
                      fieldPath,
                      raw
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean),
                    );
                  else if (field.type === "number" || field.type === "integer")
                    onChange(fieldPath, raw === "" ? "" : Number(raw));
                  else onChange(fieldPath, raw);
                }}
              />
            )}
            {field.description && (
              <span className="block text-xs leading-relaxed text-muted-foreground">
                {field.description}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
